import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { resolveImageUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ImagePlus, Eye, Calendar, Store, Phone as PhoneIcon } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAdminAxios = () => {
    const c = JSON.parse(sessionStorage.getItem("adminCredentials") || "{}");
    return axios.create({
        baseURL: `${API_URL}/api`,
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Basic ' + btoa(`${c.username || "admin"}:${c.password || ""}`) }
    });
};

const STATUS_OPTIONS = [
    { value: "active", label: "Active", color: "bg-green-500" },
    { value: "maintenance", label: "In Maintenance", color: "bg-yellow-500" },
    { value: "archived", label: "Hidden/Archived", color: "bg-red-500" },
];

const ImageUploader = ({ images, onImagesChange }) => {
    const [uploading, setUploading] = useState(false);
    const [urlInput, setUrlInput] = useState("");
    const [dragOver, setDragOver] = useState(false);

    const uploadFile = async (file) => {
        const credStr = sessionStorage.getItem('adminCredentials');
        if (!credStr) return null;
        const { username, password } = JSON.parse(credStr);
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API_URL}/api/admin/upload-image`, {
            method: 'POST',
            headers: { 'Authorization': 'Basic ' + btoa(`${username}:${password}`) },
            body: formData
        });
        if (!res.ok) throw new Error('Upload failed');
        return res.json();
    };

    const handleFiles = async (files) => {
        const valid = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (!valid.length) return toast.error("Only image files allowed");
        setUploading(true);
        const urls = [];
        for (const file of valid) {
            try {
                const r = await uploadFile(file);
                urls.push(`${API_URL}${r.url}`);
            } catch { toast.error(`Failed: ${file.name}`); }
        }
        if (urls.length) { onImagesChange([...images, ...urls]); toast.success(`${urls.length} uploaded`); }
        setUploading(false);
    };

    return (
        <div className="space-y-3">
            <div
                onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onClick={() => !uploading && document.getElementById('rental-img-input').click()}
                className={`border-2 border-dashed p-4 text-center cursor-pointer transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'} ${uploading ? 'opacity-50' : ''}`}
            >
                <input id="rental-img-input" type="file" multiple accept="image/*" className="hidden" onChange={e => handleFiles(e.target.files)} />
                {uploading ? <p className="text-sm text-muted-foreground">Uploading...</p> : (
                    <div className="flex flex-col items-center gap-1">
                        <ImagePlus className="h-6 w-6 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground"><span className="text-primary font-bold">Click</span> or drag & drop</p>
                    </div>
                )}
            </div>
            <div className="flex gap-2">
                <Input value={urlInput} onChange={e => setUrlInput(e.target.value)} className="rounded-none bg-input border-transparent focus:border-primary flex-1" placeholder="Or paste image URL..." onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (urlInput.trim()) { onImagesChange([...images, urlInput.trim()]); setUrlInput(""); }}}} />
                <Button type="button" variant="outline" className="rounded-none" onClick={() => { if (urlInput.trim()) { onImagesChange([...images, urlInput.trim()]); setUrlInput(""); }}}>Add</Button>
            </div>
            {images.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {images.map((img, i) => (
                        <div key={i} className="relative group cursor-pointer" onClick={() => { if (i === 0) return; const r = [...images]; const [m] = r.splice(i, 1); r.unshift(m); onImagesChange(r); }}>
                            <img src={resolveImageUrl(img)} alt="" className={`w-16 h-16 object-cover ${i === 0 ? "ring-2 ring-primary" : "hover:ring-1 hover:ring-muted-foreground"}`} />
                            <button type="button" onClick={e => { e.stopPropagation(); onImagesChange(images.filter((_, idx) => idx !== i)); }} className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-2.5 w-2.5" /></button>
                            {i === 0 && <span className="absolute bottom-0 left-0 right-0 bg-primary/80 text-primary-foreground text-[8px] text-center font-mono">MAIN</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const emptyMachine = {
    model: "", year: new Date().getFullYear(), serial_number: "", description: "", includes: "",
    images: [], videos: [], daily_rate: 300, weekly_rate: 1100, monthly_rate: 3450,
    deposit_amount: 100, buffer_hours: 48, status: "active",
    accessories: [], order: 0
};

const emptyAccessory = { id: "", name: "", description: "", price_per_day: 0, price_per_week: 0, image: "" };

const AdminRentals = () => {
    const [machines, setMachines] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyMachine);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [videoInput, setVideoInput] = useState("");
    const [manualDialogOpen, setManualDialogOpen] = useState(false);
    const [manualForm, setManualForm] = useState({
        machine_id: "", start_date: "", end_date: "",
        customer_name: "", customer_phone: "", customer_email: "",
        notes: "", source: "walk_in"
    });
    const [manualSaving, setManualSaving] = useState(false);

    const fetch = async () => {
        try {
            const [mRes, bRes] = await Promise.all([
                getAdminAxios().get('/admin/rentals/machines'),
                getAdminAxios().get('/admin/rentals/bookings')
            ]);
            setMachines(mRes.data);
            setBookings(bRes.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetch(); }, []);

    const openNew = () => { setEditing(null); setForm({ ...emptyMachine }); setDialogOpen(true); };
    const openEdit = (m) => { setEditing(m); setForm({ ...emptyMachine, ...m }); setDialogOpen(true); };

    const save = async () => {
        if (!form.model || !form.serial_number) return toast.error("Model and serial number required");
        try {
            const api = getAdminAxios();
            if (editing) {
                await api.put(`/admin/rentals/machines/${editing.id}`, form);
                toast.success("Machine updated");
            } else {
                await api.post('/admin/rentals/machines', form);
                toast.success("Machine added");
            }
            setDialogOpen(false);
            fetch();
        } catch { toast.error("Save failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this rental machine?")) return;
        try { await getAdminAxios().delete(`/admin/rentals/machines/${id}`); toast.success("Deleted"); fetch(); }
        catch { toast.error("Delete failed"); }
    };

    const addAccessory = () => {
        setForm({ ...form, accessories: [...form.accessories, { ...emptyAccessory, id: crypto.randomUUID() }] });
    };

    const updateAccessory = (idx, field, val) => {
        const acc = [...form.accessories];
        acc[idx] = { ...acc[idx], [field]: val };
        setForm({ ...form, accessories: acc });
    };

    const removeAccessory = (idx) => {
        setForm({ ...form, accessories: form.accessories.filter((_, i) => i !== idx) });
    };

    const addVideo = () => {
        if (!videoInput.trim()) return;
        setForm({ ...form, videos: [...(form.videos || []), videoInput.trim()] });
        setVideoInput("");
    };

    const openManualBooking = () => {
        setManualForm({
            machine_id: machines.length > 0 ? machines[0].id : "",
            start_date: "", end_date: "",
            customer_name: "", customer_phone: "", customer_email: "",
            notes: "", source: "walk_in"
        });
        setManualDialogOpen(true);
    };

    const saveManualBooking = async () => {
        if (!manualForm.machine_id || !manualForm.start_date || !manualForm.end_date) {
            return toast.error("Machine and dates are required");
        }
        setManualSaving(true);
        try {
            await getAdminAxios().post('/admin/rentals/bookings/manual', {
                ...manualForm,
                start_date: new Date(manualForm.start_date).toISOString(),
                end_date: new Date(manualForm.end_date).toISOString(),
            });
            toast.success("Manual booking created — calendar is now blocked");
            setManualDialogOpen(false);
            fetch();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create booking");
        } finally {
            setManualSaving(false);
        }
    };

    const deleteBooking = async (id) => {
        if (!window.confirm("Delete this booking? This will free up the dates on the calendar.")) return;
        try {
            await getAdminAxios().delete(`/admin/rentals/bookings/${id}`);
            toast.success("Booking deleted");
            fetch();
        } catch { toast.error("Delete failed"); }
    };

    const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

    const bookingStatusColor = (s) => {
        const m = { confirmed: "bg-green-500", active: "bg-blue-500", pending: "bg-yellow-500", returned: "bg-purple-500", completed: "bg-gray-500", cancelled: "bg-red-500" };
        return m[s] || "bg-gray-500";
    };

    return (
        <div className="space-y-6" data-testid="admin-rentals-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-foreground">Rental Fleet</h1>
                    <p className="text-muted-foreground text-sm">{machines.length} machines · {bookings.length} bookings</p>
                </div>
            </div>

            <Tabs defaultValue="machines">
                <TabsList className="rounded-none bg-card border border-border w-full justify-start">
                    <TabsTrigger value="machines" className="rounded-none data-[state=active]:bg-primary/10" data-testid="tab-machines">
                        Machines ({machines.length})
                    </TabsTrigger>
                    <TabsTrigger value="bookings" className="rounded-none data-[state=active]:bg-primary/10" data-testid="tab-bookings">
                        <Calendar className="h-4 w-4 mr-1" /> Bookings ({bookings.length})
                    </TabsTrigger>
                </TabsList>

                {/* ========== MACHINES TAB ========== */}
                <TabsContent value="machines" className="mt-4">
                    <div className="flex justify-end mb-4">
                        <Button onClick={openNew} className="rounded-none uppercase font-bold tracking-wider" data-testid="add-rental-btn">
                            <Plus className="mr-2 h-4 w-4" /> Add Machine
                        </Button>
                    </div>

            {loading ? (
                <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-card border border-border animate-pulse" />)}</div>
            ) : (
                <div className="space-y-2">
                    {machines.map(m => {
                        const st = STATUS_OPTIONS.find(s => s.value === m.status) || STATUS_OPTIONS[0];
                        return (
                            <div key={m.id} className="bg-card border border-border p-4 flex items-center gap-4" data-testid={`rental-row-${m.id}`}>
                                {m.images?.[0] ? <img src={resolveImageUrl(m.images[0])} alt="" className="w-16 h-16 object-contain bg-muted/30" /> : <div className="w-16 h-16 bg-muted/30 flex items-center justify-center text-xs text-muted-foreground">No img</div>}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-foreground">{m.model} ({m.year})</span>
                                        <Badge className={`${st.color} text-white text-[10px] rounded-none`}>{st.label}</Badge>
                                    </div>
                                    <p className="text-muted-foreground text-xs">S/N: {m.serial_number} | Buffer: {m.buffer_hours}h</p>
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="font-mono text-primary text-sm">${m.daily_rate}/day | ${m.weekly_rate}/wk</p>
                                    <p className="text-muted-foreground text-xs">{m.accessories?.length || 0} accessories</p>
                                </div>
                                <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" onClick={() => openEdit(m)}><Pencil className="h-4 w-4" /></Button>
                                    <Button variant="ghost" size="icon" onClick={() => remove(m.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
                </TabsContent>

                {/* ========== BOOKINGS TAB ========== */}
                <TabsContent value="bookings" className="mt-4">
                    <div className="flex justify-end mb-4">
                        <Button onClick={openManualBooking} className="rounded-none uppercase font-bold tracking-wider gap-2" data-testid="add-manual-booking-btn">
                            <Store className="h-4 w-4" /> Add Walk-in / Phone Booking
                        </Button>
                    </div>

                    {bookings.length === 0 ? (
                        <div className="text-center py-16 bg-card border border-border">
                            <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">No bookings yet</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {bookings.map(b => (
                                <div key={b.id} className="bg-card border border-border p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid={`booking-row-${b.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                            <span className="font-bold text-foreground">{b.booking_number}</span>
                                            <Badge className={`${bookingStatusColor(b.status)} text-white text-[10px] rounded-none`}>
                                                {(b.status || "pending").toUpperCase()}
                                            </Badge>
                                            {b.source === "walk_in" && (
                                                <Badge variant="outline" className="text-[10px] rounded-none gap-1"><Store className="h-3 w-3" />Walk-in</Badge>
                                            )}
                                            {b.source === "phone" && (
                                                <Badge variant="outline" className="text-[10px] rounded-none gap-1"><PhoneIcon className="h-3 w-3" />Phone</Badge>
                                            )}
                                            {b.payment_status === "manual" && (
                                                <Badge variant="outline" className="text-[10px] rounded-none border-yellow-500 text-yellow-500">Manual</Badge>
                                            )}
                                        </div>
                                        <p className="text-sm text-muted-foreground">{b.machine_name || b.machine_model || "Machine"}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {formatDate(b.start_date)} → {formatDate(b.end_date)}
                                            {b.customer && <span> · {b.customer.first_name} {b.customer.last_name} {b.customer.email ? `(${b.customer.email})` : ""}</span>}
                                        </p>
                                        {b.notes && <p className="text-xs text-yellow-500 mt-1">Note: {b.notes}</p>}
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="font-mono text-primary text-sm">${(b.pricing?.subtotal || b.total_cost || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                                        <p className="text-xs text-muted-foreground">{b.pricing?.days || "—"} days</p>
                                    </div>
                                    <div className="flex gap-1">
                                        <select
                                            value={b.status}
                                            onChange={async (e) => {
                                                try {
                                                    await getAdminAxios().put(`/admin/rentals/bookings/${b.id}`, { status: e.target.value });
                                                    toast.success("Status updated");
                                                    fetch();
                                                } catch { toast.error("Update failed"); }
                                            }}
                                            className="rounded-none bg-input border-transparent focus:border-primary h-8 px-2 text-xs"
                                            data-testid={`booking-status-${b.id}`}
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="confirmed">Confirmed</option>
                                            <option value="active">Active</option>
                                            <option value="returned">Returned</option>
                                            <option value="completed">Completed</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                        <Button variant="ghost" size="icon" onClick={() => deleteBooking(b.id)} data-testid={`delete-booking-${b.id}`}>
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Manual Booking Dialog */}
            <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
                <DialogContent className="max-w-lg bg-background border-border rounded-none">
                    <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2">
                            <Store className="h-5 w-5" /> Add Walk-in / Phone Booking
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Machine *</Label>
                            <select
                                value={manualForm.machine_id}
                                onChange={e => setManualForm({ ...manualForm, machine_id: e.target.value })}
                                className="w-full rounded-none bg-input border-transparent focus:border-primary h-10 px-3 text-sm mt-1"
                                data-testid="manual-booking-machine"
                            >
                                {machines.map(m => (
                                    <option key={m.id} value={m.id}>{m.model} ({m.year}) — S/N: {m.serial_number}</option>
                                ))}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Start Date *</Label>
                                <Input type="date" value={manualForm.start_date} onChange={e => setManualForm({ ...manualForm, start_date: e.target.value })} className="rounded-none bg-input border-transparent focus:border-primary mt-1" data-testid="manual-booking-start" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">End Date *</Label>
                                <Input type="date" value={manualForm.end_date} onChange={e => setManualForm({ ...manualForm, end_date: e.target.value })} className="rounded-none bg-input border-transparent focus:border-primary mt-1" data-testid="manual-booking-end" />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Source</Label>
                            <select
                                value={manualForm.source}
                                onChange={e => setManualForm({ ...manualForm, source: e.target.value })}
                                className="w-full rounded-none bg-input border-transparent focus:border-primary h-10 px-3 text-sm mt-1"
                                data-testid="manual-booking-source"
                            >
                                <option value="walk_in">Walk-in Store</option>
                                <option value="phone">Phone Order</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Customer Name</Label>
                            <Input value={manualForm.customer_name} onChange={e => setManualForm({ ...manualForm, customer_name: e.target.value })} className="rounded-none bg-input border-transparent focus:border-primary mt-1" placeholder="John Doe" data-testid="manual-booking-name" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone</Label>
                                <Input value={manualForm.customer_phone} onChange={e => setManualForm({ ...manualForm, customer_phone: e.target.value })} className="rounded-none bg-input border-transparent focus:border-primary mt-1" placeholder="555-1234" data-testid="manual-booking-phone" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                                <Input value={manualForm.customer_email} onChange={e => setManualForm({ ...manualForm, customer_email: e.target.value })} className="rounded-none bg-input border-transparent focus:border-primary mt-1" placeholder="email@example.com" data-testid="manual-booking-email" />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
                            <Textarea value={manualForm.notes} onChange={e => setManualForm({ ...manualForm, notes: e.target.value })} className="rounded-none bg-input border-transparent focus:border-primary mt-1" rows={2} placeholder="Any notes about this rental..." data-testid="manual-booking-notes" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-none" onClick={() => setManualDialogOpen(false)}>Cancel</Button>
                        <Button className="rounded-none uppercase font-bold tracking-wider" onClick={saveManualBooking} disabled={manualSaving} data-testid="manual-booking-save-btn">
                            {manualSaving ? "Saving..." : "Block Calendar & Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit/Create Machine Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-background border-border rounded-none">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{editing ? "Edit" : "Add"} Rental Machine</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6">
                        {/* Basic Info */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Model *</Label><Input value={form.model} onChange={e => setForm({...form, model: e.target.value})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" placeholder="Cold Jet Aero V" /></div>
                            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Year *</Label><Input type="number" value={form.year} onChange={e => setForm({...form, year: parseInt(e.target.value) || 0})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" /></div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Serial Number *</Label><Input value={form.serial_number} onChange={e => setForm({...form, serial_number: e.target.value})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" /></div>
                            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Includes</Label><Input value={form.includes} onChange={e => setForm({...form, includes: e.target.value})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" placeholder="Air hose, nozzle kit" /></div>
                        </div>
                        <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label><Textarea value={form.description || ""} onChange={e => setForm({...form, description: e.target.value})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" rows={3} /></div>

                        {/* Status & Buffer */}
                        <div className="grid grid-cols-3 gap-4">
                            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
                                <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full rounded-none bg-input border-transparent focus:border-primary h-10 px-3 text-sm mt-1">
                                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                </select>
                            </div>
                            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Buffer Hours</Label><Input type="number" value={form.buffer_hours} onChange={e => setForm({...form, buffer_hours: parseInt(e.target.value) || 24})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" /></div>
                            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Display Order</Label><Input type="number" value={form.order} onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" /></div>
                        </div>

                        {/* Rates */}
                        <div>
                            <h3 className="font-heading font-bold text-foreground mb-2">Pricing</h3>
                            <div className="grid grid-cols-4 gap-4">
                                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Daily Rate ($)</Label><Input type="number" value={form.daily_rate} onChange={e => setForm({...form, daily_rate: parseFloat(e.target.value) || 0})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" /></div>
                                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Weekly Rate ($)</Label><Input type="number" value={form.weekly_rate} onChange={e => setForm({...form, weekly_rate: parseFloat(e.target.value) || 0})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" /></div>
                                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Monthly Rate ($)</Label><Input type="number" value={form.monthly_rate} onChange={e => setForm({...form, monthly_rate: parseFloat(e.target.value) || 0})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" /></div>
                                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Deposit ($)</Label><Input type="number" value={form.deposit_amount} onChange={e => setForm({...form, deposit_amount: parseFloat(e.target.value) || 100})} className="rounded-none bg-input border-transparent focus:border-primary mt-1" /></div>
                            </div>
                        </div>

                        {/* Images */}
                        <div>
                            <h3 className="font-heading font-bold text-foreground mb-2">Images</h3>
                            <ImageUploader images={form.images || []} onImagesChange={imgs => setForm({...form, images: imgs})} />
                        </div>

                        {/* Videos */}
                        <div>
                            <h3 className="font-heading font-bold text-foreground mb-2">Videos</h3>
                            <div className="flex gap-2 mb-2">
                                <Input value={videoInput} onChange={e => setVideoInput(e.target.value)} className="rounded-none bg-input border-transparent focus:border-primary flex-1" placeholder="YouTube URL (e.g. https://youtube.com/watch?v=...)" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addVideo(); }}} />
                                <Button type="button" variant="outline" className="rounded-none" onClick={addVideo}>Add</Button>
                            </div>
                            {(form.videos || []).map((v, i) => (
                                <div key={i} className="flex items-center gap-2 mb-1 text-sm">
                                    <span className="text-muted-foreground truncate flex-1">{v}</span>
                                    <button onClick={() => setForm({...form, videos: form.videos.filter((_, idx) => idx !== i)})} className="text-destructive hover:text-destructive/80"><X className="h-4 w-4" /></button>
                                </div>
                            ))}
                        </div>

                        {/* Accessories */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-heading font-bold text-foreground">Accessories</h3>
                                <Button type="button" variant="outline" size="sm" className="rounded-none" onClick={addAccessory}><Plus className="h-3 w-3 mr-1" /> Add</Button>
                            </div>
                            {form.accessories?.map((acc, i) => (
                                <div key={i} className="border border-border p-3 mb-2 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground">Accessory {i + 1}</span>
                                        <button onClick={() => removeAccessory(i)} className="text-destructive"><X className="h-4 w-4" /></button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Input value={acc.name} onChange={e => updateAccessory(i, 'name', e.target.value)} placeholder="Name" className="rounded-none bg-input border-transparent focus:border-primary text-sm" />
                                        <Input value={acc.description} onChange={e => updateAccessory(i, 'description', e.target.value)} placeholder="Description" className="rounded-none bg-input border-transparent focus:border-primary text-sm" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground whitespace-nowrap">$/day:</span><Input type="number" value={acc.price_per_day} onChange={e => updateAccessory(i, 'price_per_day', parseFloat(e.target.value) || 0)} className="rounded-none bg-input border-transparent focus:border-primary text-sm" /></div>
                                        <div className="flex items-center gap-2"><span className="text-xs text-muted-foreground whitespace-nowrap">$/wk:</span><Input type="number" value={acc.price_per_week} onChange={e => updateAccessory(i, 'price_per_week', parseFloat(e.target.value) || 0)} className="rounded-none bg-input border-transparent focus:border-primary text-sm" /></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button className="rounded-none uppercase font-bold tracking-wider" onClick={save}>{editing ? "Save Changes" : "Add Machine"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminRentals;
